/* ============================================================
   CHRONCES — <cr-buy-form>
   Galerie + quantité + ajout au panier de la fiche produit.

   L'assurance est ajoutée DANS LA MÊME requête que le produit :
   une seule ligne d'historique, et pas de fenêtre où le client
   aurait payé l'assurance sans le motif.
   ============================================================ */

import { CartLinesUpdateEvent } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';

const SECTION_ID = 'cart-drawer-section';

class CrBuyForm extends HTMLElement {
  connectedCallback() {
    this.qty = 1;
    this.output = this.querySelector('[data-cr-qty-value]');
    this.decBtn = this.querySelector('[data-cr-qty-dec]');
    this.incBtn = this.querySelector('[data-cr-qty-inc]');
    this.addBtn = this.querySelector('[data-cr-add]');
    this.insurance = this.querySelector('[data-cr-ins-inline]');
    this.added = this.querySelector('[data-cr-added]');

    this.decBtn?.addEventListener('click', () => this.setQty(this.qty - 1));
    this.incBtn?.addEventListener('click', () => this.setQty(this.qty + 1));
    this.addBtn?.addEventListener('click', this.onAdd);
    this.insurance?.addEventListener('click', () => {
      this.insurance.classList.toggle('is-checked');
      this.insurance.setAttribute('aria-pressed', this.insurance.classList.contains('is-checked'));
      const box = this.insurance.querySelector('[data-cr-ins-box]');
      if (box) box.textContent = this.insurance.classList.contains('is-checked') ? '✓' : '';
      this.refreshTotal();
    });

    // Galerie
    this.mainImage = this.querySelector('[data-cr-gallery-main]');
    this.querySelectorAll('[data-cr-gallery-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => this.selectMedia(thumb));
    });

    this.setQty(1);
  }

  selectMedia(thumb) {
    if (!this.mainImage) return;
    const src = thumb.dataset.src;
    const srcset = thumb.dataset.srcset;
    if (src) this.mainImage.src = src;
    if (srcset) this.mainImage.srcset = srcset;
    this.mainImage.alt = thumb.dataset.alt || '';
    this.querySelectorAll('[data-cr-gallery-thumb]').forEach((t) => t.setAttribute('aria-current', String(t === thumb)));
  }

  setQty(value) {
    const max = parseInt(this.dataset.max || '0', 10);
    this.qty = Math.max(1, max > 0 ? Math.min(value, max) : value);
    if (this.output) this.output.textContent = this.qty;
    if (this.decBtn) this.decBtn.disabled = this.qty <= 1;
    if (this.incBtn) this.incBtn.disabled = max > 0 && this.qty >= max;
    this.refreshTotal();
  }

  refreshTotal() {
    const target = this.querySelector('[data-cr-total]');
    if (!target) return;
    const unit = parseInt(this.dataset.price || '0', 10);
    const insurancePrice = this.insuranceChecked ? parseInt(this.dataset.insurancePrice || '0', 10) : 0;
    const total = unit * this.qty + insurancePrice;
    target.textContent = this.formatMoney(total);
  }

  get insuranceChecked() {
    return Boolean(this.insurance?.classList.contains('is-checked'));
  }

  // Les prix Shopify sont en centimes. On aligne le rendu sur le filtre `money`
  // du thème (« 11,00€ », sans espace avant le symbole) : sinon le total du
  // bouton s'écrit différemment du prix affiché juste au-dessus.
  formatMoney(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
        style: 'currency',
        currency: this.dataset.currency || 'EUR',
      })
        .format(cents / 100)
        .replace(/[\s  ]+(?=\D*$)/, '');
    } catch {
      return `${(cents / 100).toFixed(2).replace('.', ',')}€`;
    }
  }

  onAdd = async () => {
    if (this.busy) return;
    const variantId = this.dataset.variantId;
    if (!variantId) return;

    this.busy = true;
    this.classList.add('is-busy');

    const items = [{ id: Number(variantId), quantity: this.qty }];
    const insuranceId = this.dataset.insuranceVariantId;
    if (this.insuranceChecked && insuranceId) items.push({ id: Number(insuranceId), quantity: 1 });

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ id: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const body = JSON.stringify({ items, sections: SECTION_ID, sections_url: window.location.pathname });
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));

      if (!response.ok) {
        let reason = `HTTP ${response.status}`;
        try {
          const err = await response.clone().json();
          reason = err.description || err.message || reason;
        } catch {
          /* réponse HTML : on garde le code */
        }
        throw new Error(reason);
      }

      const json = await response.json();
      // `/cart/add.js` ne renvoie pas un panier complet (ni devise ni total) :
      // on relit /cart.js, sinon createCartFromAjaxResponse lève.
      const cart = await (await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })).json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { sections: json.sections, itemCount: cart.item_count, source: 'cr-buy-form', didError: false },
      });

      if (json.sections?.[SECTION_ID]) morphSection(SECTION_ID, json.sections[SECTION_ID], { mode: 'hydration' });

      if (this.added) {
        this.added.hidden = false;
        setTimeout(() => { this.added.hidden = true; }, 4000);
      }
      document.querySelector('#cart-drawer')?.open?.();
    } catch (error) {
      console.error('[cr-buy-form]', error);
      deferred.reject(error);
      if (this.added) {
        this.added.hidden = false;
        this.added.textContent = "Ajout impossible pour le moment — réessaie dans un instant.";
        setTimeout(() => { this.added.hidden = true; }, 5000);
      }
    } finally {
      this.classList.remove('is-busy');
      this.busy = false;
    }
  };
}

if (!customElements.get('cr-buy-form')) customElements.define('cr-buy-form', CrBuyForm);
